import {useSuspenseQuery} from '@tanstack/react-query'
import {memo, Suspense, useMemo} from 'react'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import clsx from 'clsx'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
} from '~/utils/product'
import {getPriceFromPriceString} from '~/client/spreadshop/old/hacks'
import {defaultImagesQueryOptions} from '~/client/fn/defaultImages'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import useShopConfig from '~/hooks/useShopConfig'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {ImageFormat} from '~/gql/graphql'

type ProductCollectionItemProps = {
    product: {
        id: string
        vId: string
        name: string
        productTypeId: string
        productTypeName: string
        defaultAppearanceId?: string
        defaultViewId?: string
        price: string
    }
    appearance?: string
}

type ProductCollectionProps = {
    className?: string
    headline: {
        label: string
        isHidden: boolean
    }
    items: ProductCollectionItemProps[]
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
}

const ProductCollection = ({className, headline, items}: ProductCollectionProps) => {
    return (
        <div className={clsx('mx-auto max-w-screen-xl px-4 md:px-6', className)}>
            <h2
                className={clsx(
                    headline.isHidden ? 'sr-only' : 'text-2xl mb-4 md:text-4xl font-bold'
                )}
            >
                {headline.label}
            </h2>
            <Suspense fallback={<ProductCollectionCarouselSkeletonLoader />}>
                <ProductCarousel items={items} />
            </Suspense>
        </div>
    )
}

type ProductCarouselProps = {
    items: ProductCollectionItemProps[]
}

const ProductCarousel = ({items}: ProductCarouselProps) => {
    const params = useLocaleParams()
    const shopConfig = useShopConfig()
    const {data: defaultImages} = useSuspenseQuery(defaultImagesQueryOptions(params.locale))
    const itemsWithImage = useMemo(() => {
        return items.map((item) => {
            const productTypeImage = defaultImages.find(
                (image) => image.productTypeId === item.product.productTypeId
            )?.images

            const baseProductImageSrc = `${shopConfig.imageServerEndpoint}/products/${item.product.vId}`
            const baseDesignImageSrc = getCompositionImageBaseUrl(baseProductImageSrc)!

            const appearanceId = item.appearance ?? item.product.defaultAppearanceId
            const viewId = item.product.defaultViewId ?? '1'

            return {
                name: item.product.name,
                price: getPriceFromPriceString(item.product.price),
                productId: item.product.id,
                productTypeName: item.product.productTypeName,
                appearanceId: appearanceId,
                image: {
                    src: getProductModelImageSrc(
                        baseProductImageSrc,
                        viewId,
                        appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    srcset: getProductModelImageSrcSet(
                        baseProductImageSrc,
                        viewId,
                        appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    altText: item.product.name,
                    format: ImageFormat.Png,
                },
                designImage: {
                    src: getDesignImageSrc(baseDesignImageSrc, viewId, appearanceId),
                    srcset: getDesignImageSrcSet(baseDesignImageSrc, viewId, appearanceId),
                    altText: item.product.name,
                },
            }
        })
    }, [items, defaultImages, shopConfig.imageServerEndpoint])

    return (
        <div className="relative">
            <Carousel
                config={config}
                showDots={false}
                showDisabledArrows={true}
                initialSlidesToShow={3.25}
            >
                {itemsWithImage.map((item, index) => (
                    <Link
                        key={item.productId}
                        className="block h-full group outline-none"
                        to="/$lang/products/$productId"
                        params={{...params, productId: item.productId}}
                        search={{
                            appearanceId: item.appearanceId,
                        }}
                    >
                        <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-purple-600 transition duration-200 ease-in-out transform-gpu">
                            <ImageComposition
                                className="overflow-hidden"
                                imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                lazy={index > 3}
                                image={item.image}
                                sizes="500px"
                            />
                            <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                <ImageComposition
                                    className="rounded-md overflow-hidden bg-white"
                                    image={item.designImage!}
                                    lazy={true}
                                    sizes="500px"
                                />
                            </div>
                            <div className="flex gap-4 flex-row justify-between mx-4 mt-4 mb-2 text-sm flex-shrink-0">
                                <div className="truncate text-gray-400">{item.name}</div>
                                <Price className="text-nowrap font-medium">
                                    {item.price?.vatIncluded}
                                </Price>
                            </div>
                            <div className="mx-4  mb-4 line-clamp-1 text-sm flex-grow">
                                {item.productTypeName}
                            </div>
                        </Card>
                    </Link>
                ))}
            </Carousel>
        </div>
    )
}

const ProductCollectionCarouselSkeletonLoader = memo(() => {
    return (
        <div>
            <Carousel
                config={{...config, all: {...config.all, draggable: false}}}
                showDots={false}
                showArrows={false}
            >
                {[...new Array(8)].map((_, index) => (
                    <Card key={index} className="p-4" aria-label="Loading content...">
                        <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                        <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                    </Card>
                ))}
            </Carousel>
        </div>
    )
})

export default ProductCollection
